import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { CentralizedPrintJob } from "@/lib/centralized-print-queue";

export interface PrintQueueStats {
  pending: number;
  failed: number;
  printed: number;
}

export function usePrintQueueMonitor() {
  const { activeRestaurant } = useRestaurantContext();
  const [jobs, setJobs] = useState<CentralizedPrintJob[]>([]);
  const [stats, setStats] = useState<PrintQueueStats>({ pending: 0, failed: 0, printed: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    if (!activeRestaurant?.id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("pending_print_jobs")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) {
        console.error("[PRINT-MONITOR] Error fetching jobs:", error);
        return;
      }

      const jobsData = (data || []) as CentralizedPrintJob[];
      setJobs(jobsData);

      // Calculate stats
      const pending = jobsData.filter(j => j.status === "pending").length;
      const failed = jobsData.filter(j => j.status === "failed").length;
      const printed = jobsData.filter(j => j.status === "printed").length;
      setStats({ pending, failed, printed });
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant?.id]);

  // Retry a failed job (set back to pending)
  const retryJob = useCallback(async (jobId: string) => {
    const { error } = await supabase
      .from("pending_print_jobs")
      .update({ status: "pending", error_message: null })
      .eq("id", jobId);

    if (error) {
      console.error("[PRINT-MONITOR] Error retrying job:", error);
      return false;
    }

    await fetchJobs();
    return true;
  }, [fetchJobs]);

  // Retry all failed jobs
  const retryAllFailed = useCallback(async () => {
    if (!activeRestaurant?.id) return false;

    const { error } = await supabase
      .from("pending_print_jobs")
      .update({ status: "pending", error_message: null })
      .eq("restaurant_id", activeRestaurant.id)
      .eq("status", "failed");

    if (error) {
      console.error("[PRINT-MONITOR] Error retrying all failed jobs:", error);
      return false;
    }

    await fetchJobs();
    return true;
  }, [activeRestaurant?.id, fetchJobs]);

  // Delete a job
  const deleteJob = useCallback(async (jobId: string) => {
    const { error } = await supabase
      .from("pending_print_jobs")
      .delete()
      .eq("id", jobId);

    if (error) {
      console.error("[PRINT-MONITOR] Error deleting job:", error);
      return false;
    }

    await fetchJobs();
    return true;
  }, [fetchJobs]);

  // Delete all printed jobs
  const clearPrinted = useCallback(async () => {
    if (!activeRestaurant?.id) return false;

    const { error } = await supabase
      .from("pending_print_jobs")
      .delete()
      .eq("restaurant_id", activeRestaurant.id)
      .eq("status", "printed");

    if (error) {
      console.error("[PRINT-MONITOR] Error clearing printed jobs:", error);
      return false;
    }

    await fetchJobs();
    return true;
  }, [activeRestaurant?.id, fetchJobs]);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Realtime subscription for updates
  useEffect(() => {
    if (!activeRestaurant?.id) return;

    const channel = supabase
      .channel("print-queue-monitor")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "pending_print_jobs",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        () => {
          fetchJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurant?.id, fetchJobs]);

  return {
    jobs,
    stats,
    isLoading,
    fetchJobs,
    retryJob,
    retryAllFailed,
    deleteJob,
    clearPrinted,
  };
}
